import { For, Match, Show, Signal, Switch, createContext, createEffect, createSignal, on, useContext } from "solid-js"
import { dataSourceEditor as api } from 'st-app/api'
import styles from './st-data-source-editor.module.css'
import ButtonGroup from "../st-button-group/st-button-group"
import STButton, { ButtonType } from "../st-button/st-button"
import STCheckbox from "../st-checkbox/st-checkbox"
import STInput from "../st-input/st-input"
import { createProxySignal } from "common"
import { QueryPreviewResultColumn } from "st-app/api"
import ParameterEditor, { Parameter } from "./parameter-editor"
import { SetStoreFunction, createStore } from "solid-js/store"
import STModal from "../st-modal/st-modal"
import STQueryEditor from "../st-query-editor/st-query-editor"

export type DataSourceType = 'SQL Query' | 'ST Query' | 'Extract Data'

export class STDataSourceContext {
    type: Signal<DataSourceType>
    query: Signal<string>
    shouldCache: Signal<boolean>
    shouldUseSampleData: Signal<boolean>
    noResultsMessage: Signal<string>
    maximumPreviewRows: Signal<number | undefined>
    executionTime: Signal<number | undefined>
    previewData: Signal<QueryPreviewResultColumn[]>

    parameters: Record<string, Parameter>
    setParameters: SetStoreFunction<typeof this.parameters>

    queryError: Signal<string>

    constructor(
        type: Signal<DataSourceType>,
        query: Signal<string>,
        shouldCache: Signal<boolean>,
        shouldUseSampleData: Signal<boolean>,
        noResultsMessage: Signal<string>,
        maximumPreviewRows: Signal<number | undefined>
    ) {
        this.type = type;
        this.query = query;
        this.shouldCache = shouldCache;
        this.shouldUseSampleData = shouldUseSampleData;
        this.noResultsMessage = noResultsMessage;
        this.maximumPreviewRows = maximumPreviewRows;
        this.queryError = createSignal('');
        this.executionTime = createSignal<number | undefined>(0);
        this.previewData = createSignal<QueryPreviewResultColumn[]>([]);
        [this.parameters, this.setParameters] = createStore<Record<string, Parameter>>({});
    }
}

const Context = createContext<STDataSourceContext>()

const TEST_QUERY = 'Student R0, Student R1, K12.AccommodationInfo.VA.FCPSCurrentStudentIEP R3 COLS R0.AddressValidationFlag, R0.AdultEdDiplomaType, R0.AICEDiploma'

export class STDataSourceEditorProps {
    type?: Signal<DataSourceType>
    query?: Signal<string>
    shouldCache?: Signal<boolean>
    shouldUseSampleData?: Signal<boolean>
    noResultsMessage?: Signal<string>
    maximumPreviewRows?: Signal<number | undefined>
}

export default function STDataSourceEditor(props: STDataSourceEditorProps) {
    const type = props.type ?? createSignal<DataSourceType>('ST Query')
    const query = props.query ?? createSignal(TEST_QUERY)
    const shouldCache = props.shouldCache ?? createSignal<boolean>(false)
    const shouldUseSampleData = props.shouldUseSampleData ?? createSignal<boolean>(false)
    const noResultsMessage = props.noResultsMessage ?? createSignal<string>("")
    const maximumPreviewRows = props.maximumPreviewRows ?? createSignal<number | undefined>(undefined)

    const context = new STDataSourceContext(type, query, shouldCache, shouldUseSampleData, noResultsMessage, maximumPreviewRows)

    const [queryError] = context.queryError

    const [previewData] = context.previewData

    //Input expects Signal<string>, but we want to keep this as a Signal<number>
    const maxRowsProxy = createProxySignal(
        () => `${context.maximumPreviewRows[0]()}`,
        (v) => context.maximumPreviewRows[1](Number.parseInt(v))
    );
    
    return (
        <Context.Provider value={context}>
            <div class={styles['st-data-source-editor']}>
                <QueryTypeSelector />
                <div style='display: grid; grid-template-columns: 1fr 1fr; gap: 8px'>
                    <QueryEditor />
                    <ParameterEditor />
                </div>

                <Show when={queryError()}>
                    <span class={styles['query-error']}>{queryError()}</span>
                </Show>

                <div class='flexbox horizontal' style='justify-content: space-between;'>
                    <div>
                        <h5>Caching</h5>
                        <STCheckbox label="Cache this query" value={context.shouldCache} />
                    </div>

                    <div>
                        <h5>Sample Data</h5>
                        <STCheckbox label="Use sample data for report configuration" value={context.shouldUseSampleData} />
                    </div>

                    <div>
                        <h5>No Results Message</h5>
                        <STInput value={context.noResultsMessage} />
                    </div>

                    <div>
                        <h5>Maximum Preview Results</h5>
                        <STInput type='number' value={maxRowsProxy} />
                    </div>
                </div>

                <Show when={context.type[0]() === 'SQL Query'}>
                    <div>
                        <SQLQueryHelpMessage />
                    </div>
                </Show>

                <h3>Preview Results</h3>

                <div>
                    <table class={styles['preview-table']}>
                        <thead>
                            <tr>
                                <th>Column Name</th>
                                <th>Value</th>
                            </tr>
                        </thead>

                        <tbody>
                            <For each={previewData()}>{(col) => (
                                <tr>
                                    <td>{col.name}</td>
                                    <td>{col.value}</td>
                                </tr>
                            )}</For>
                        </tbody>
                    </table>
                </div>
            </div>
        </Context.Provider>
    )
}

STDataSourceEditor.defaultProps = new STDataSourceEditorProps()

const SQLQueryHelpMessage = () => {
    return (
        <p style='font-size: 12px;'>
            SQL Statements with UPDATE, INSERT, or DELETE are not allowed.
            <br/>
            Focus parameters include 
            <b>@GRADINGPERIODID</b>, <b>@ENROLLMENTPERIODID</b>, <b>@CLASSID</b>, <b>@SCHOOLYEARID</b>, <b>@SCHOOLID</b>, 
            <b>@TEACHERID</b>, <b>@ORGANIZATION_YEAR_GU</b>, <b>@ORGANIZATION_GU</b>, <b>@FOCUS_ORGANIZATION_GU</b>, 
            <b>@FOCUS_ORG_YEAR_GU_LIST</b>, <b>@YEAR_GU</b>, <b>@FOCUS_YEAR_GU</b>, <b>@FOCUS_SCHOOL_YEAR</b>, 
            <b>@FOCUS_STUDENT_ACTIVE_STATE</b>(Active Only = 0, Inactive Only = 1, Active And Inactive = 2) 
            AND <b>@USER_GU</b>.
        </p>
    )
}

const QueryTypeSelector = () => {
    const ctx = useDataSourceEditor()
    const [type, setType] = ctx.type

    return (
        <ButtonGroup>
            <STButton type="primary" active={type() === 'SQL Query'} onClick={() => setType('SQL Query')}>SQL Query</STButton>
            <STButton type="primary" active={type() === 'ST Query'} onClick={() => setType('ST Query')}>Synergy Query</STButton>
        </ButtonGroup>
    )
}

const QueryEditor = () => {
    const ctx = useDataSourceEditor()
    const [type, setType] = ctx.type
    const [query, setQuery] = ctx.query

    const [executionTime, setExecutionTime] = ctx.executionTime
    const [data, setData] = ctx.previewData
    const [error, setError] = ctx.queryError

    const [queryDesignerOpen, setQueryDesignerOpen] = createSignal(true)


    createEffect(on(() => [query()], async () => {
        const q = query()

        if (!q) {
            setExecutionTime(undefined)
            setData([])
            setError('')
        } else {

            try {
                const result = await api.PreviewDataSource(q, type())
                setExecutionTime(result.executionTimeInSeconds)
                setData(result.results)
                setError('')
            } catch(e) {
                if (e instanceof Error) {
                    setError(e.message)
                } else {
                    setError('An error occurred while executing the query.')
                }
            }
        }
    }))

    return (
        <div class='flexbox' style='min-height: 300px; gap: 8px'>
            <Switch>
                <Match when={type() === 'SQL Query'}>
                    <div class='flexbox horizontal middle' style='font-size:13px'>
                        <b> Query </b>
                        <Show when={executionTime()}>
                            <span> (Last Execution Time: {executionTime()} seconds) </span>
                        </Show>
                    </div>
                    <textarea class={styles['sql-query-textarea']} value={query()} onChange={(e) => setQuery(e.target.value)}></textarea>
                </Match>
                <Match when={type() === 'ST Query'}>
                    <div class='flexbox horizontal middle' style='font-size:13px; justify-content: space-between;'>
                        <b> Query </b>
                        <div class='flexbox horizontal' style='gap: 8px;'>
                            <STButton onClick={() => { setQueryDesignerOpen(true) }}>Query Designer</STButton>
                            <STButton onClick={() => { }}>Validate</STButton>
                        </div>
                    </div>
                    <textarea class={styles['sql-query-textarea']} value={query()} onChange={(e) => setQuery(e.target.value)}></textarea>
                    <STModal title='Query' show={[queryDesignerOpen, setQueryDesignerOpen]}>
                        <div class='flexbox' style='width: 90dvw; height: 85dvh; padding-top: 8px;'>
                            <STQueryEditor query={[query, setQuery]} />
                        </div>
                    </STModal>
                </Match>
            </Switch>
        </div>
    )
}

export const useDataSourceEditor = () => {
    const ctx = useContext(Context)
    if (!ctx) {
        throw new Error('useDataSourceEditor must be used within a STDataSourceEditor')
    }

    return ctx
}